import { makeStyles } from '@material-ui/styles'
import clsx from 'clsx'
import React, { PropsWithChildren, useCallback, useEffect, useMemo, useState } from 'react'
import useReactRouter from 'use-react-router'
import { HeaderTitlePortal } from 'core/elements/header/portals'
import { Route } from 'core/plugins/route'
import Theme from 'core/themes/model'
import { onlyDefinedValues } from '../../../utils/fp'
import { TabContext } from './TabContext'
import TabPreview from './TabPreview'

interface Props {
  // Use these props if getting active tab from url
  route?: Route
  routeKey?: string

  // Use these props if active tab is passed in (eg. card tabs)
  activeTab?: string
  setActiveTab?: (value) => void

  // use this flag if you want the tab previews to be portaled to the header
  previewInHeader?: boolean

  // use this flag to keep all tabs mounted (hidden with display:none) for form validation
  keepTabsInView?: boolean

  // use for any additional cb logic you need
  onClick?: (tab: string) => void
}

export default function Tabs({
  route,
  routeKey = 'tab',
  activeTab,
  setActiveTab,
  onClick,
  children,
  previewInHeader,
  keepTabsInView = false,
}: PropsWithChildren<Props>) {
  const {
    history,
    match: { params },
  } = useReactRouter()
  const sanitizedParams: any = useMemo(() => onlyDefinedValues(params), [params])
  const classes = useStyles()
  const currentTab = route && routeKey ? params[routeKey] : activeTab
  const [tabs, setTabs] = useState([])

  const addTab = useCallback(
    (tab) => {
      setTabs((tabs) => Array.from(new Set([...tabs, tab])))
    },
    [setTabs],
  )

  const updateTab = useCallback(
    (tabValue, fields) => {
      setTabs((tabs) =>
        tabs.map((tab) => {
          if (tab.value === tabValue) {
            return { ...tab, ...fields }
          }
          return tab
        }),
      )
    },
    [setTabs],
  )

  const handleClick = useCallback(
    (tab) => {
      if (!!onClick) {
        onClick(tab)
      }
      if (route && routeKey) {
        history.push(route.path({ ...sanitizedParams, [routeKey]: tab }))
      } else {
        setActiveTab(tab)
      }
    },
    [route, routeKey, params, setActiveTab],
  )

  // if the current tab is hidden, redirect to the first tab
  useEffect(() => {
    const activeTab = tabs?.find((tab) => tab?.value === currentTab)

    if (activeTab?.hidden) {
      handleClick(tabs[0]?.value)
    }
  }, [tabs, currentTab, handleClick])

  const providerValue = useMemo(
    () => ({
      activeTab: currentTab,
      addTab,
      updateTab,
      keepTabsInView,
    }),
    [currentTab, addTab, updateTab, keepTabsInView],
  )

  const Container = previewInHeader ? HeaderTitlePortal : React.Fragment
  return (
    <>
      <Container>
        <div
          className={clsx(classes.tabsPreviewContainer, {
            [classes.tabsPreviewContainerInHeader]: previewInHeader,
          })}
        >
          {tabs.map((tab) =>
            !tab.hidden ? (
              <TabPreview
                key={tab.value}
                value={tab.value}
                label={tab.label}
                count={tab.count}
                isActive={currentTab === tab.value}
                onClick={handleClick}
              />
            ) : null,
          )}
        </div>
      </Container>
      <TabContext.Provider value={providerValue}>{children}</TabContext.Provider>
    </>
  )
}

const useStyles = makeStyles<Theme>((theme) => ({
  tabsPreviewContainer: {
    display: 'grid',
    gridAutoFlow: 'column',
    justifyContent: 'start',
    gap: 4,
    borderBottom: `1px solid ${theme.components.tab.border}`,
    marginBottom: 16,
    marginTop: 0,
  },
  tabsPreviewContainerInHeader: {
    marginBottom: 0,
    marginTop: 16,
  },
}))
